import {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import Dinero from 'dinero.js'
import {Flex, Input, Radio, RadioGroup, Stack, Text} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import CurrencyInput from 'react-currency-input-field'
import input from '~/react/theme/components/input'
import FormikFieldWrapper from '~/react/components/FormikFieldWrapper'
import {ExpandablePanel} from '../ExpandablePanel'
import {formPropTypes} from '../formPropTypes'
import {PayablePropType} from '../../../../propTypes'

const PaymentDetailsForm = ({
  payable,
  values,
  isValid,
  setFieldValue,
  setFieldTouched,
}) => {
  const {t} = useTranslation('pointsOnPayments')
  const [paymentType, setPaymentType] = useState('full')

  const fullBalance = payable?.dueAmount?.getAmount() || 0

  const paymentAmount = Dinero({
    amount: parseInt(values.amount || 0, 10),
  })

  useEffect(() => {
    if (paymentType === 'full' && fullBalance) {
      setFieldValue('amount', fullBalance)
    }
  }, [paymentType, fullBalance, setFieldValue])

  return (
    <ExpandablePanel
      title={t(
        'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.title',
      )}
      isDisabled={!isValid}
      completedClosedContent={
        <Flex justify="space-between" width="100%" color="mld.neutral.700">
          <Flex direction="column" align="start">
            <Text textStyle="body-detail" textTransform="uppercase">
              {t(
                'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.closedContent.referenceLabel',
              )}
            </Text>
            <Text textStyle="body-copy">{values.reference}</Text>
          </Flex>
          <Flex direction="column" align="end">
            <Text textStyle="body-detail" textTransform="uppercase">
              {t(
                'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.closedContent.amountLabel',
              )}
            </Text>
            <Text textStyle="body-copy">{paymentAmount?.toFormat()}</Text>
          </Flex>
        </Flex>
      }
    >
      <Flex direction="column" gap="xs" pb="md">
        <Text textStyle="body-copy-medium">
          {t(
            'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.fields.amount.label',
          )}
        </Text>
        <RadioGroup
          name="paymentType"
          value={paymentType}
          variant="mld"
          onChange={(value) => {
            setPaymentType(value)
          }}
        >
          <Stack spacing="xs">
            <Flex
              {...input.variants.outline().field}
              justify="space-between"
              align="center"
              p="sm"
              cursor="pointer"
              onClick={() => {
                setPaymentType('full')
              }}
            >
              <Radio value="full">
                <Text textStyle="body-copy">
                  {t(
                    'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.payFullBalance',
                  )}
                </Text>
              </Radio>
              <Text textStyle="body-copy-medium">
                {payable?.dueAmount?.toFormat()}
              </Text>
            </Flex>
            <Flex
              {...input.variants.outline().field}
              cursor="pointer"
              direction="column"
              p="sm"
              gap="sm"
            >
              <Radio
                value="partialPayment"
                onClick={() => {
                  setPaymentType('partialPayment')
                }}
              >
                <Text textStyle="body-copy">
                  {t(
                    'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.payDifferentAmount',
                  )}
                </Text>
              </Radio>
              {paymentType === 'partialPayment' && (
                <FormikFieldWrapper name="amount" pt="0" l="md">
                  <Input
                    as={CurrencyInput}
                    data-testid="partial-payment-amount-input"
                    defaultValue={
                      values.amount && (values.amount / 100).toFixed(2)
                    }
                    decimalsLimit={2}
                    intlConfig={{locale: 'en-AU', currency: 'AUD'}}
                    onValueChange={(_value, _name, {float}) => {
                      setFieldValue('amount', float * 100)
                    }}
                    onBlur={() => {
                      setFieldTouched('amount', true)
                    }}
                  />
                </FormikFieldWrapper>
              )}
            </Flex>
          </Stack>
        </RadioGroup>
      </Flex>
      <FormikFieldWrapper
        as={Input}
        variant="outline"
        name="reference"
        label={t(
          'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.fields.reference.label',
        )}
      />
    </ExpandablePanel>
  )
}

PaymentDetailsForm.propTypes = {
  payable: PayablePropType,
  values: formPropTypes.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
}

PaymentDetailsForm.defaultProps = {
  payable: null,
}

export default PaymentDetailsForm
